<template>
  <div v-show="show">
    <div v-if="labelPosition !== 'hidden'" class="font-bold">{{ field.name }}</div>
    <FormKit
      ref="fileInput"
      v-model="files"
      outer-class="flex flex-col"
      :inner-class="'order-[-1] ' + field.allowedKinds[0]"
      help-class="order-[-2] mb-2 text-sm text-app-green"
      :help-position="helpPosition"
      :help="field.instructions"
      :max="limitFiles || undefined"
      :multiple="limitFiles !== 1"
      :name="name"
      type="file"
      :accept="allowedMimes.join(',')"
      validation-visibility="dirty"
      :validation="validation"
      :validation-rules="{ combinedSizeRule, maxSizeRule }"
      :validation-label="field.name"
      :validation-messages="{
        combinedSizeRule: ({ args }: { args: number[] }) => $t('validation.file-upload.combined-size-too-large', { size: args[0] }),
        maxSizeRule: ({ args }: { args: number[] }) => $t('validation.file-upload.file-too-large', { size: args[0] }),
      }"
      @input="handleFileChange"
    />
    <button
      v-if="limitFiles !== 1 && fileCount > 0 && fileCount < limitFiles"
      type="button"
      class="mt-3 text-sm text-app-green formkit-inner w-full p-2 justify-center"
      @click="triggerFileDialog"
    >
      + Add file
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { fieldHelpPosition, fieldLabelPosition, fieldName, fieldValidationArray, fieldVisible } from '~/assets/formie'

interface FormieField {
  name: string
  instructions?: string
  allowedKinds: string[]
  limitFiles?: string
  [key: string]: any
}

interface Props {
  settings: {
    combinedSizeLimit?: number
    [key: string]: any
  }
  parentField?: object
  field: FormieField
}

const props = defineProps<Props>()

const mimeMap: Record<string, string[]> = {
  image: ['image/jpg', 'image/jpeg', 'image/png', 'image/gif', 'image/svg', 'image/svg+xml', 'image/webp'],
  audio: ['audio/mpeg', 'audio/vorbis'],
  captionSubtitles: ['text/vtt'],
  compressed: ['application/zip'],
  excel: ['application/vnd.ms-excel'],
  json: ['application/json', 'application/jsonld'],
  javascript: ['text/javascript'],
  pdf: ['application/pdf'],
  powerpoint: ['application/vnd.ms-powerpoint'],
  text: ['text/plain', 'text/csv', 'text/html'],
  video: ['video/mp4'],
  word: ['application/msword'],
}

const allowedMimes = props.field.allowedKinds.flatMap((kind) => mimeMap[kind] || [])
const limitFiles = Number.parseInt(props.field.limitFiles || '0')
const combinedSize = ref(0)
const files = ref<File[]>([])
const fileInput = ref<any>(null)

const fileCount = computed(() => files.value.length)

const maxSizeRule = (node: any) => {
  const valids = node.value.map((value: { file: { size: number } }): boolean => value.file.size < props.field.sizeLimit * 1024 * 1024)
  return valids.length === 0 || valids.every(Boolean)
}
const combinedSizeRule = (_node: any, limit: number) => {
  return combinedSize.value < limit * 1024 * 1024
}

const handleFileChange = (newFiles: File[]) => {
  files.value = newFiles
  combinedSize.value = newFiles.reduce((sum, file) => sum + file.size, 0)
}

const helpPosition = fieldHelpPosition(props.settings, props.field)
const labelPosition = fieldLabelPosition(props.settings, props.field)
const name = fieldName(props.field, props.parentField)
const show = fieldVisible(props.field)
const validation = [
  ...fieldValidationArray(props.field),
  props.settings.combinedSizeLimit ? ['combinedSizeRule', props.settings.combinedSizeLimit] : undefined,
  props.field.sizeLimit ? ['maxSizeRule', props.field.sizeLimit] : undefined,
].filter((rule): rule is string | [string, ...any[]] => rule !== undefined)

function triggerFileDialog() {
  const input = fileInput.value?.$el?.querySelector('input[type=file]') as HTMLInputElement | null
  if (!input) return

  input.onchange = (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
    const target = e.target as HTMLInputElement
    const newFiles = Array.from(target.files || [])
    files.value = [...files.value, ...newFiles]
    target.value = ''
  }
  input.click()
}
</script>
